import { z } from "zod";

export const PLATFORMS = ["youtube", "instagram", "facebook", "tiktok", "x"] as const;
export type Platform = (typeof PLATFORMS)[number];

export const WordSchema = z.object({
  text: z.string(),
  startMs: z.number(),
  endMs: z.number(),
  speaker: z.string().optional(),
  emphasis: z.boolean().optional(),
});
export type Word = z.infer<typeof WordSchema>;

export const SegmentSchema = z.object({ inMs: z.number(), outMs: z.number() });
export type Segment = z.infer<typeof SegmentSchema>;
/** A sound effect placed at a source moment (ms into the lecture), at its own level. */
export const SfxMomentSchema = z.object({ id: z.string(), atMs: z.number(), soundId: z.string(), levelDb: z.number().default(0) });
export type SfxMoment = z.infer<typeof SfxMomentSchema>;

export const CropKfSchema = z.object({ ms: z.number(), cx: z.number(), cy: z.number(), zoom: z.number() });
export type CropKf = z.infer<typeof CropKfSchema>;

export const CaptionCardSchema = z.object({
  id: z.string(),
  startMs: z.number(),
  endMs: z.number(),
  words: z.array(WordSchema),
});
export type CaptionCard = z.infer<typeof CaptionCardSchema>;

export const CutStyleSchema = z.enum(["hard", "punch", "dissolve", "whip", "zoom", "flash", "slide", "blur"]);
export type CutStyle = z.infer<typeof CutStyleSchema>;
export const CutTransitionSchema = z.object({ afterMs: z.number(), style: CutStyleSchema, ms: z.number().optional() });
export type CutTransition = z.infer<typeof CutTransitionSchema>;

export const ClipEditsSchema = z.object({
  /** playback order of the pieces as their source in-points, duplicates allowed (a piece can play twice); empty = source order */
  arrangement: z.array(z.number()).default([]),
  /** source ranges where the music must be silent (Quran, a du'a, a moment that needs quiet) */
  musicMutes: z.array(SegmentSchema).default([]),
  trimStartMs: z.number(),
  trimEndMs: z.number(),
  /** source ranges removed by hand */
  manualCuts: z.array(SegmentSchema).default([]),
  /** auto-cut silences the editor chose to keep */
  keptGaps: z.array(SegmentSchema).default([]),
  /** text overrides keyed by the word's source startMs */
  wordText: z.record(z.string(), z.string()).default({}),
  /** forced caption card breaks (timeline ms of the word that starts a new card) */
  cardBreaks: z.array(z.number()).default([]),
  /** manual punch-zoom moments (source ms) */
  zoomMarks: z.array(z.number()).default([]),
  /** manual split points (source ms): a piece boundary the editor can select and delete either side of */
  splits: z.array(z.number()).default([]),
});
export type ClipEdits = z.infer<typeof ClipEditsSchema>;

export const FacePointSchema = z.object({ ms: z.number(), cx: z.number(), cy: z.number(), w: z.number() });

export const PlatformMetaSchema = z.object({
  title: z.string(),
  /** alternative titles the owner can swap in (the AI writes three) */
  titleOptions: z.array(z.string()).default([]),
  description: z.string(),
  hashtags: z.array(z.string()),
});
export type PlatformMeta = z.infer<typeof PlatformMetaSchema>;

export const StyleSchema = z.object({
  /** a caption preset id (presets.ts) or "custom"; clips saved before the ids lost their "hiyc-" prefix are read as the new ids */
  preset: z.string().default("boxed").transform((v) => v.replace(/^hiyc-/, "")),
  font: z.string().default("Inter Tight"),
  fontSize: z.number().default(54),
  weight: z.number().default(700),
  color: z.string().default("#F7F5EF"),
  highlight: z.string().default("#F5C542"),
  /** how the word being spoken is marked */
  highlightMode: z.enum(["color", "pill", "underline", "glow", "none"]).default("color"),
  /** how emphasised words are marked */
  emphasisStyle: z.enum(["underline", "bigger", "color", "none"]).default("color"),
  stroke: z.string().default("#0B0F0D"),
  strokeWidth: z.number().default(0),
  shadow: z.boolean().default(false),
  box: z.enum(["none", "pill", "box", "glass"]).default("box"),
  boxColor: z.string().default("#0B0F0D"),
  boxOpacity: z.number().default(0.62),
  animation: z.enum(["pop", "fade", "slide", "none"]).default("fade"),
  align: z.enum(["center", "left"]).default("center"),
  /** fraction of the frame width the caption block may use */
  maxWidth: z.number().default(0.86),
  lineHeight: z.number().default(1.15),
  letterSpacing: z.number().default(-0.5),
  /** vertical centre of the caption block, 0..1 of frame height */
  yPos: z.number().default(0.74),
  /** horizontal nudge of the caption block, composition px */
  xOffset: z.number().default(0),
  caseMode: z.enum(["as-is", "upper"]).default("as-is"),
  wordsPerCard: z.number().int().default(7),
  /** a second line under the words in another language (Arabic under English): that version's translation of the same card */
  secondLang: z.string().nullable().default(null),
  /** the second line's size as a share of the caption size */
  secondScale: z.number().min(0.4).max(1).default(0.68),
  /** the second line's colour; null = the caption colour */
  secondColor: z.string().nullable().default(null),
});
export type CaptionStyle = z.infer<typeof StyleSchema>;

/** Extra caption languages a clip can be made in; English is always the edited master. */
export const VERSION_LANGS = ["ar", "tr", "ur", "fr", "id", "so", "bs"] as const;

/** One extra-language version of a clip: translated caption text per English card id (with timed words when it comes from the lecture's own language), opener text, end line and post captions. */
export const VersionCardSchema = z.object({ text: z.string(), words: z.array(WordSchema).nullable().default(null) });
export const VersionSchema = z.object({
  cards: z.record(z.string(), VersionCardSchema).default({}),
  hook: z.object({ text: z.string().default(""), subtitle: z.string().default("") }).prefault({}),
  endLine: z.string().default(""),
  meta: z.record(z.string(), PlatformMetaSchema).default({}),
  /** fingerprint of the English captions and texts this was translated from; differs when the English edit changed since */
  source: z.string().default(""),
  translatedAt: z.string().default(""),
  from: z.enum(["source", "claude"]).default("claude"),
});
export type ClipVersion = z.infer<typeof VersionSchema>;

export const BEHIND_ANIMATIONS = ["rise", "fade", "none"] as const;
/** A word or short phrase drawn behind the speaker while it shows: the speaker's cutout covers it, so it reads as part of the room. */
export const BehindTextSchema = z.object({
  id: z.string(),
  text: z.string().default(""),
  /** source ms: when it shows */
  startMs: z.number(),
  endMs: z.number(),
  /** null = the clip's caption font */
  font: z.string().nullable().default(null),
  /** px on the 1080-wide canvas */
  size: z.number().min(40).max(600).default(190),
  color: z.string().default("#F7F5EF"),
  /** words wrapped in *stars* take this colour */
  accentColor: z.string().default("#F5C542"),
  opacity: z.number().min(0.1).max(1).default(0.85),
  uppercase: z.boolean().default(true),
  animation: z.enum(BEHIND_ANIMATIONS).default("rise"),
  /** nudge from the default spot (centred, upper third), px */
  offsetX: z.number().default(0),
  offsetY: z.number().default(0),
  enabled: z.boolean().default(true),
});
export type BehindText = z.infer<typeof BehindTextSchema>;

export const OVERLAY_KINDS = ["money", "place", "mosque", "person", "object", "concept"] as const;
export const OVERLAY_STYLES = ["card", "full", "corner"] as const;
/** A reference image over the video while something is being spoken about. Images live in clips/<id>/overlays/. */
export const OVERLAY_ENTERS = ["fade", "slide", "pop", "rise"] as const;
export const OVERLAY_MOTIONS = ["push", "drift", "still"] as const;
export const OverlaySchema = z.object({
  id: z.string(),
  /** source ms: when it is shown */
  startMs: z.number(),
  endMs: z.number(),
  /** what is being referred to, e.g. "Masjid an-Nabawi", "gold coins" */
  subject: z.string().default(""),
  kind: z.enum(OVERLAY_KINDS).default("object"),
  /** the image prompt (the AI writes one from the words when empty) */
  prompt: z.string().default(""),
  /** every generated option, oldest first; `image` is the one in use */
  images: z.array(z.string()).default([]),
  image: z.string().nullable().default(null),
  /** a picture fills the whole screen while it shows; a card or a corner is the owner's own choice for one picture */
  style: z.enum(OVERLAY_STYLES).default("full"),
  /** the library picture this one is a copy of, so the same picture is never shown twice in one lecture */
  libraryId: z.string().nullable().default(null),
  enabled: z.boolean().default(true),
  offsetX: z.number().default(0),
  offsetY: z.number().default(0),
  scale: z.number().default(1),
  from: z.enum(["ai", "manual"]).default("manual"),
  createdAt: z.string().default(""),
  /** how it arrives: a fade, a slide in from the side, a pop, or a rise */
  enter: z.enum(OVERLAY_ENTERS).default("fade"),
  /** how it moves while it shows: a slow push in, a slow drift across, or still */
  motion: z.enum(OVERLAY_MOTIONS).default("push"),
  /** a card that follows the speaker's face (from the face tracking) instead of sitting in the free band */
  follow: z.boolean().default(false),
  /** AI B-roll videos made for this slot (mp4 files beside the images); `video` is the one in use, and it beats the still */
  videos: z.array(z.string()).default([]),
  video: z.string().nullable().default(null),
});

export type Overlay = z.infer<typeof OverlaySchema>;

export const MOMENT_KINDS = ["punch", "push", "flash", "shake", "glitch", "sparkle", "ring", "leak", "quiet"] as const;
export type MomentKind = (typeof MOMENT_KINDS)[number];
export const MOODS = ["calm", "story", "warning", "uplifting", "solemn", "energetic"] as const;
export type Mood = (typeof MOODS)[number];
/** One directed beat: an instant (punch, flash, shake, glitch) at `atMs`, or a window (push, sparkle, ring, leak, quiet) from `atMs` to `endMs`; source ms. Beats are silent: the only sounds a clip plays are on the opener and the end card. */
export const MomentSchema = z.object({
  id: z.string(),
  atMs: z.number(),
  endMs: z.number().nullable().default(null),
  kind: z.enum(MOMENT_KINDS),
  intensity: z.number().min(0).max(1).default(0.7),
  why: z.string().default(""),
});
export type Moment = z.infer<typeof MomentSchema>;
export const DirectionSchema = z.object({
  mood: z.enum(MOODS).default("calm"),
  /** older clips carried "sound" beats (and per-cut sounds, now simply ignored); nothing plays on the words any more, so those beats are dropped on load */
  moments: z.preprocess((v) => (Array.isArray(v) ? v.filter((m) => !(m && typeof m === "object" && (m as { kind?: unknown }).kind === "sound")) : v), z.array(MomentSchema)).default([]),
  /** per-clip seed for the small variations (zoom amount, tilt, timing) that keep clips from looking alike */
  seed: z.number().int().default(0),
  from: z.enum(["ai", "manual", "none"]).default("none"),
  createdAt: z.string().default(""),
  note: z.string().default(""),
});
export type Direction = z.infer<typeof DirectionSchema>;

export const ClipSchema = z.object({
  id: z.string(),
  videoId: z.string(),
  origin: z.enum(["auto", "manual"]).default("auto"),
  status: z.enum(["candidate", "editing", "approved", "rendered", "posted", "rejected"]).default("candidate"),
  sourceStartMs: z.number(),
  sourceEndMs: z.number(),
  segments: z.array(SegmentSchema),
  /** source-ms words of the clip (incl. a little slack), the editable primitive */
  words: z.array(WordSchema).default([]),
  silences: z.array(z.object({ startMs: z.number(), endMs: z.number() })).default([]),
  facePoints: z.array(FacePointSchema).default([]),
  edits: ClipEditsSchema.optional(),
  cropOffset: z.object({ dx: z.number(), dy: z.number() }).prefault({ dx: 0, dy: 0 }),
  /** a manual zoom on top of the framing, pinched or nudged on the preview (1 = as framed) */
  frameZoom: z.number().min(1).max(2.5).default(1),
  /** one-click picture fixes, applied to the footage before the render (the preview follows once its pre-cut is remade) */
  fixes: z.object({
    /** upscale to 1920 wide when the lecture is smaller, denoise lightly and sharpen */
    sharpen: z.boolean().default(false),
    /** steady a handheld or wobbly camera */
    stabilise: z.boolean().default(false),
    /** even out exposure and lift dull colour */
    colour: z.boolean().default(false),
  }).prefault({}),
  /** the room behind the speaker: as filmed, blurred, darkened, or replaced with a colour (needs the speaker cutout) */
  background: z.object({
    mode: z.enum(["room", "blur", "dark", "colour"]).default("room"),
    /** how strong the blur or the darkening is, 0..1 */
    amount: z.number().min(0).max(1).default(0.6),
    colour: z.string().default("#1B4D2E"),
  }).prefault({}),
  zoomStrength: z.number().default(1.08),
  /** auto = static unless the speaker really moves; static = one fixed crop; track = follow the face */
  follow: z.enum(["auto", "static", "track"]).default("auto"),
  zoomsPerClip: z.number().int().default(3),
  /** colour look applied to the footage */
  look: z.object({ preset: z.enum(["natural", "warm", "golden", "cool", "cinematic", "soft", "mono"]).default("natural"), vignette: z.number().default(0.25), strength: z.number().default(1) }).prefault({}),
  /** how each cut lands: plain jump cut or a small punch-in that settles */
  /** how one piece hands over to the next: hard cut, punch-in, or a real transition over transitionMs (every cut without its own) */
  cutStyle: CutStyleSchema.default("hard"),
  transitionMs: z.number().default(300),
  /** cuts with a transition of their own, each keyed by the source out-point of the piece it follows; ms falls back to transitionMs */
  cutTransitions: z.array(CutTransitionSchema).default([]),
  effects: z.object({
    /** slow zoom across each piece */
    drift: z.boolean().default(false),
    /** film grain overlay 0..1 */
    grain: z.number().default(0),
    /** cinematic letterbox bars */
    bars: z.boolean().default(false),
    /** a short camera shake on every cut */
    shake: z.boolean().default(false),
    /** a white flash frame on every cut */
    flash: z.boolean().default(false),
    /** a warm light leak drifting across the frame */
    lightLeak: z.boolean().default(false),
    /** old-projector brightness flicker */
    flicker: z.boolean().default(false),
    /** a few frames of colour-split glitch on every cut */
    glitch: z.boolean().default(false),
    /** slow golden sparkles floating up */
    sparkle: z.boolean().default(false),
    /** a thin golden ring framing the speaker */
    ring: z.boolean().default(false),
  }).prefault({}),
  /** video-time ms (after any opener) of the still used as the cover/thumbnail; null = 1 s in */
  coverMs: z.number().nullable().default(null),
  /** the clip's direction: a mood and a few timed moments (a punch here, a slow push there, a sound on some cuts) placed from the speech */
  direction: DirectionSchema.prefault({}),
  /** reference images shown over the video while something is mentioned (money, a place, a mosque, a person by symbol) */
  overlays: z.array(OverlaySchema).default([]),
  /** source ranges the transcript marks as Quran recitation or quotation (auto-detected; the music ducks out there) */
  quranSpans: z.array(SegmentSchema).default([]),
  /** which detector version wrote quranSpans (0 = never scanned); older clips are re-scanned when opened */
  quranDetector: z.number().default(0),
  /** the speaker's own audio: gain in dB (0 = as recorded) and a mute */
  /** the speaker's level, dB. The house rule is −6 on every clip, always. */
  voiceDb: z.number().default(-6),
  voiceMuted: z.boolean().default(false),
  /** background-noise cleaning of the speaker's track: how hard (0–100), and an optional stretch of source ms that is pure room noise to learn from */
  voiceClean: z.object({
    enabled: z.boolean().default(false),
    amount: z.number().min(0).max(100).default(50),
    sampleStartMs: z.number().nullable().default(null),
    sampleEndMs: z.number().nullable().default(null),
    /** one-tap polish: rumble out, a little clarity, and levelling so quiet words are not lost (its own switch, with or without the cleaning) */
    enhance: z.boolean().default(false),
  }).prefault({}),
  /** a short fade on the speaker's voice at the start and the end of the speech, ms (0 = none) */
  voiceFade: z.object({ inMs: z.number().min(0).max(3000).default(0), outMs: z.number().min(0).max(3000).default(0) }).prefault({}),
  /** auto = the picker chooses the catchiest still; manual = the owner chose a frame */
  coverPick: z.enum(["auto", "manual"]).default("auto"),
  /** the picker's best stills, best first (video-time ms) */
  coverCandidates: z.array(z.object({ ms: z.number(), score: z.number() })).default([]),
  layout: z.enum(["crop", "split"]).default("crop"),
  crop: z.array(CropKfSchema).default([]),
  captions: z.array(CaptionCardSchema).default([]),
  /** The opener: a full-screen title card before or over the first words, or a small title over the running video. */
  hook: z.object({
    text: z.string(),
    subtitle: z.string().default(""),
    durationMs: z.number().default(2200),
    /** the opener waits this long before it comes in (ms): the frame settles, then the title. House rule: 0.3 s. */
    delayMs: z.number().min(0).max(3000).default(300),
    enabled: z.boolean().default(true),
    /** card/blur/split/type fill the frame; panel/badge/big sit over the running video */
    style: z.enum(["card", "blur", "split", "type", "panel", "badge", "big"]).default("blur"),
    /** intro = the speech starts after the opener; over = the opener covers the first words */
    mode: z.enum(["intro", "over"]).default("intro"),
    bg: z.enum(["ink", "green", "gold", "white"]).default("green"),
    textColor: z.string().default("#F7F5EF"),
    accentColor: z.string().default("#F5C542"),
    align: z.enum(["center", "left", "bottom"]).default("center"),
    animation: z.enum(["rise", "fade", "zoom", "words", "none"]).default("rise"),
    transition: z.enum(["fade", "cut", "slide"]).default("fade"),
    /** percent of the style's base size */
    fontSize: z.number().default(100),
    font: z.string().default("Inter Tight"),
    showLogo: z.boolean().default(true),
    showHandle: z.boolean().default(false),
    /** nudge of the opener text from its layout position, composition px (dragged on the video) */
    offsetX: z.number().default(0),
    offsetY: z.number().default(0),
    /** a small line above the title ("THE VIRTUE OF" before "HAJJ") */
    kicker: z.string().default(""),
    /** who is speaking ("w/ Sh. Abu Ayman"); filled from the lecture when empty and showCredit is on */
    credit: z.string().default(""),
    showCredit: z.boolean().default(false),
    /** a small band above the title ("RAMADAN SERIES") */
    ribbon: z.string().default(""),
    /** a short tag in the corner ("QnA") */
    badge: z.string().default(""),
    /** where the tag sits: a nudge (px) from its corner, and its tilt (degrees) */
    badgeX: z.number().default(0),
    badgeY: z.number().default(0),
    badgeAngle: z.number().min(-45).max(45).default(-4),
    badgeBg: z.string().default("#C8102E"),
    badgeColor: z.string().default("#FFFFFF"),
  }),
  endCard: z
    .object({
      enabled: z.boolean().default(true),
      line: z.string().default("Follow for daily reminders"),
      /** the organisation's social handle (set from config.org.handle when a clip is made) */
      handle: z.string().default(""),
      /** a quieter second line, e.g. "Full lecture on YouTube" */
      sub: z.string().default(""),
      durationMs: z.number().default(2000),
      /** classic = logo + handle on the brand colour; poster = over the catchy still; type = huge handle; minimal = quiet; split = still on top, card below */
      style: z.enum(["classic", "poster", "type", "minimal", "split"]).default("classic"),
      bg: z.enum(["green", "ink", "gold", "white"]).default("green"),
      font: z.string().default("Inter Tight"),
      textColor: z.string().default("#F7F5EF"),
      accentColor: z.string().default("#F5C542"),
      /** percent of the style's base size */
      fontSize: z.number().default(100),
      animation: z.enum(["rise", "fade", "zoom", "none"]).default("rise"),
      showLogo: z.boolean().default(true),
      logoScale: z.number().default(1),
      /** a pill button under the handle */
      button: z.boolean().default(true),
      buttonText: z.string().default("Follow"),
      /** nudge of the text block, composition px (dragged on the video) */
      offsetX: z.number().default(0),
      offsetY: z.number().default(0),
    })
    .prefault({}),
  brand: z
    .object({
      logo: z.enum(["bc", "tc", "tl", "tr", "bl", "br", "ml", "mr", "off"]).default("bc"),
      logoScale: z.number().default(1),
      logoStyle: z.enum(["plain", "pill", "tag", "mono", "glow"]).default("plain"),
      logoOpacity: z.number().default(0.9),
      /** a logo from the studio's library chosen for this clip; null lets the studio pick one to suit the footage and the cards */
      logoId: z.string().nullable().default(null),
      /** nudge from the anchor, in composition px */
      logoX: z.number().default(0),
      logoY: z.number().default(0),
      progressBar: z.boolean().default(true),
      barPos: z.enum(["bottom", "top"]).default("bottom"),
      barColor: z.string().default("#22B14C"),
      barHeight: z.number().default(6),
    })
    .prefault({}),
  style: StyleSchema.prefault({}),
  music: z
    .object({
      trackId: z.string().nullable().default(null),
      /** house bed level: the tracks sit near −15.5 LUFS, so −20 puts the bed 10 LU under a −6 dB voice around the opener and end card */
      volumeDb: z.number().default(-20),
      /** and another 10 dB down while the speaker talks (20 LU under the voice) */
      duckDb: z.number().default(-10),
      /** steady = one lowered level for the whole speech, up only before and after it; words = dips per word run (rises in pauses) */
      duckMode: z.enum(["steady", "words"]).default("steady"),
      fadeMs: z.number().default(1500),
      /** silence the bed without forgetting the chosen track */
      muted: z.boolean().default(false),
      /** drop the music while Quran is recited or quoted (auto-detected spans plus the owner's own) */
      quranMute: z.boolean().default(true),
      /** section of the track to use (ms into the track); null endMs = to the end */
      startMs: z.number().default(0),
      endMs: z.number().nullable().default(null),
    })
    .prefault({}),
  /** the sounds a clip plays: one as the opener appears, one as the end card lands, optionally one at every cut, and any placed at a moment (library ids, or null for silence) */
  sfx: z.object({
    hook: z.boolean().default(true),
    endCard: z.boolean().default(true),
    openerId: z.string().nullable().default("whoosh"),
    /** the same whoosh closes the clip: one sound in, the same sound out */
    endCardId: z.string().nullable().default("whoosh"),
    /** a sound at every cut after the first piece (off by default: the house style is calm) */
    cuts: z.boolean().default(false),
    cutsId: z.string().nullable().default("whoosh-soft"),
    /** sounds placed by hand at a source moment */
    moments: z.array(SfxMomentSchema).default([]),
    /** level of each sound, dB (0 = the studio's default mix) */
    levels: z.object({ opener: z.number().default(0), endCard: z.number().default(0), cuts: z.number().default(-6) }).prefault({}),
    /** playback speed of every sound (0.8 deeper and longer … 1.25 brighter and shorter) */
    pitch: z.number().min(0.7).max(1.4).default(1),
  }).prefault({}),
  meta: z.record(z.string(), z.record(z.enum(["en", "ar"]), PlatformMetaSchema)).prefault({}),
  /** when a background job last wrote each group of fields, so a save from an older copy never overwrites newer work */
  stamps: z.object({ meta: z.string().optional(), versions: z.string().optional(), cover: z.string().optional(), analysis: z.string().optional(), quran: z.string().optional(), overlays: z.string().optional(), direction: z.string().optional() }).prefault({}),
  /** language of the captions in this clip object (a localised copy for preview/render); the stored clip is always English */
  captionLang: z.enum(["en", ...VERSION_LANGS]).default("en"),
  /** big words that sit BEHIND the speaker (the speaker is cut out and drawn over them); source ms like the pictures */
  behindTexts: z.array(BehindTextSchema).default([]),
  /** how each language version looks: its font (null = the studio default for that language) and caption size relative to English (null = the studio rule) */
  langStyle: z.partialRecord(z.enum(VERSION_LANGS), z.object({ font: z.string().nullable().default(null), sizeScale: z.number().min(0.6).max(1.8).nullable().default(null) })).prefault({}),
  /** extra languages the owner wants versions in */
  versionLangs: z.array(z.enum(VERSION_LANGS)).default([]),
  versions: z.partialRecord(z.enum(VERSION_LANGS), VersionSchema).prefault({}),
  plan: z
    .object({ postAt: z.string().nullable().default(null), posted: z.record(z.string(), z.string().nullable()).prefault({}) })
    .prefault({}),
  selection: z
    .object({
      scores: z.record(z.string(), z.number()),
      total: z.number(),
      /** which bar the moment cleared when it was picked: a standout stops a scroll, a meaningful one benefits whoever watches it to the end */
      tier: z.enum(["standout", "meaningful"]).optional(),
      rationale: z.string(),
      flags: z.array(z.string()),
      references: z.array(z.string()),
      emphasisWords: z.array(z.string()),
      completeness: z.string().optional(),
      /** the spoken line that hooks in the first seconds, and the post copy the clip was picked for: the promise it must keep */
      hookLine: z.string().optional(),
      title: z.string().optional(),
      caption: z.string().optional(),
    })
    .optional(),
  flags: z.array(z.string()).default([]),
  history: z.array(z.object({ ts: z.string(), note: z.string() })).default([]),
});
export type Clip = z.infer<typeof ClipSchema>;
export type ClipInput = z.input<typeof ClipSchema>;
